import React, {useEffect, useContext, useRef, useState} from 'react';
import { PDFStatusContext } from '../../../context/PDFStatusContext';
import './ViewerPdf.css';
import {logAudit} from "../../../utils/logAudit";
import {getSignatureAppearances} from "../../../services/signatureAppearanceService";
import domtoimage from 'dom-to-image';
import { useTranslation } from 'react-i18next';
import {freezeComputedSizes, isSafari, waitImagesReady} from "../../../utils/imageUtils";
import {
  getPreviousSignaturePage,
  isAllPagesModeEnabled,
  SIGNATURE_PAGE_MODE_EVENT
} from "../../../utils/signaturePageMode";

const getDPR = () => Math.max(1, (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1);
const MIN_SIGNATURE_TEMPLATE_HOST_WIDTH_PX = 520;
const MAX_SIGNATURE_TEMPLATE_HOST_WIDTH_PX = 1800;

async function waitFontsAndImages(root) {
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {}
  const imgs = Array.from(root?.querySelectorAll('img') || []);
  await Promise.all(imgs.map(i => i.decode?.().catch(() => null)));
}

async function captureNodeToPNG(node) {
  await waitFontsAndImages(node);
  await waitImagesReady(node);
  const rect = node.getBoundingClientRect();
  const dpr = getDPR();
  const restore = freezeComputedSizes(node);
  try {
    const doCapture = () => domtoimage.toPng(node, {
      bgcolor: null,
      width: Math.round(rect.width * dpr),
      height: Math.round(rect.height * dpr),
      style: {
        transform: `scale(${dpr})`,
        transformOrigin: 'top left',
        width: `${Math.round(rect.width)}px`,
        height: `${Math.round(rect.height)}px`,
      }
    });
    if (isSafari()) {
      try { await doCapture(); } catch {}
      await new Promise(r => setTimeout(r, 150));
    }
    return await doCapture();
  } finally {
    restore();
  }
}

async function renderHtmlTemplateToPNG(htmlTemplate, hostWidthPx = 0) {
  if (typeof htmlTemplate !== 'string' || !htmlTemplate.trim()) return null;

  const widthNum = Number(hostWidthPx);
  const baseWidth = Number.isFinite(widthNum) && widthNum > 0 ? Math.round(widthNum) : 420;
  const hostWidth = Math.max(MIN_SIGNATURE_TEMPLATE_HOST_WIDTH_PX, baseWidth);
  const mount = document.createElement('div');
  mount.style.position = 'fixed';
  mount.style.left = '-10000px';
  mount.style.top = '0';
  mount.style.pointerEvents = 'none';
  mount.style.zIndex = '-1';
  mount.style.background = '#ffffff';
  mount.style.width = `${hostWidth}px`;
  mount.innerHTML = `
    <div class="a-view">
      <div class="appearance-html-preview">
        <div class="appearance-html-preview__inner">${htmlTemplate}</div>
      </div>
    </div>
  `;
  document.body.appendChild(mount);

  try {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const target = mount.querySelector('.appearance-html-preview__inner') || mount.firstElementChild || mount;
    if (!target) return null;
    const preferredWidth = Math.ceil(
      Number(target.scrollWidth) ||
      Number(target.getBoundingClientRect().width) ||
      hostWidth
    );
    if (preferredWidth > hostWidth) {
      const expandedWidth = Math.min(MAX_SIGNATURE_TEMPLATE_HOST_WIDTH_PX, preferredWidth + 8);
      mount.style.width = `${expandedWidth}px`;
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
    return await captureNodeToPNG(target);
  } finally {
    mount.remove();
  }
}

async function getImageDimensionsFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({
      width: Number(img.naturalWidth || img.width || 0),
      height: Number(img.naturalHeight || img.height || 0),
    });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

const PdfViewer = ({ blobUrl, fileName }) => {
  const { i18n } = useTranslation();
  const BASE_URL = process.env.REACT_APP_REDIRECT_URI;
  const currentLanguage = i18n.resolvedLanguage || i18n.language || 'es';
  const pdfLocale = currentLanguage.toLowerCase().startsWith('en') ? 'en-US' : 'es-ES';
  const viewerUrl = `${BASE_URL}/js/pdfjs/web/viewer.html?file=${encodeURIComponent(blobUrl)}&originPage=${encodeURIComponent(window.location.pathname)}#locale=${pdfLocale}`;
  const { setViewerLoaded, setDocumentLoaded, setDraggableReady, setPdfNotProtected } = useContext(PDFStatusContext);
  const iframeRef = useRef();
  const minScale = 0.5; // La firma puede reducirse a la mitad
  const maxScale = 1.3;
  const appearanceRef = useRef(false);
  const appearanceAutoLoadRef = useRef(false);
  const [blockMessage, setBlockMessage] = useState('');
  const [docBlocked, setDocBlocked] = useState(false);
  const [docMDPLevel, setDocMDPLevel] = useState(null); // null | 1 | 2 | 3

  const sendPageModeToViewer = (detail = {}) => {
    iframeRef.current?.contentWindow?.postMessage({
      type: 'SET_ALL_PAGES_MODE',
      allPages: detail.allPages ?? isAllPagesModeEnabled(),
      previousPage: detail.previousPage ?? getPreviousSignaturePage()
    }, '*');
  };

  useEffect(() => {
    const handlePageModeChange = (event) => sendPageModeToViewer(event.detail);
    window.addEventListener(SIGNATURE_PAGE_MODE_EVENT, handlePageModeChange);
    return () => window.removeEventListener(SIGNATURE_PAGE_MODE_EVENT, handlePageModeChange);
  }, []);

  async function preScanPdfEncryption(objectUrl) {
    if (!objectUrl) return { encrypted: false };

    try {
      // Descargamos el blob del object URL
      const resp = await fetch(objectUrl);
      if (!resp.ok) throw new Error(`Blob fetch failed: ${resp.status}`);
      const buf = await resp.arrayBuffer();

      // Decodificamos como Latin-1 para no romper bytes 0x80-0xFF.
      // Podríamos escanear en binario, pero esto es suficiente y simple.
      const text = new TextDecoder('latin1', { fatal: false }).decode(new Uint8Array(buf));

      // Búsqueda básica del diccionario de cifrado del PDF.
      // /Encrypt debe existir en cualquier PDF cifrado (user u owner).
      const hasEncrypt = /\/Encrypt\b/.test(text);

      // SAFETY: No exponemos claves ni detalles; solo un booleano de cifrado.
      return { encrypted: hasEncrypt };
    } catch (e) {
      // En caso de error de lectura, NO bloqueamos por defecto (fail-open),
      // para no generar falsos positivos. El viewer.mjs seguirá siendo respaldo.
      // Si quieres, registra el error en auditoría:
      try {
        logAudit({
          state: 'WARN',
          message: 'Pre-scan de cifrado falló, se delega al viewer',
          event: 'PDF_ENCRYPTION_PRESCAN_FAILED',
          args: { error: String(e) }
        });
      } catch {}
      return { encrypted: false };
    }
  }

  // NOTE: Pre-chequeo de cifrado al cambiar el blobUrl.
// Si detectamos /Encrypt, bloqueamos el documento ANTES de cargar el viewer.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!blobUrl) return;

      const { encrypted } = await preScanPdfEncryption(blobUrl);
      if (cancelled) return;

      if (encrypted) {
        // SAFETY: Política local: si está cifrado (user/owner), no permitimos firma.
        // Disparamos el mismo circuito de bloqueo global que usas en los mensajes del viewer.
        setPdfNotProtected(false);   // dispara el warning global (SignDocument.jsx)
        setDocumentLoaded(false);
        setDraggableReady(false);
        setViewerLoaded(false);      // aquí no hay iframe cargado
        setDocBlocked(true);         // NO montamos el iframe

        try {
          logAudit({
            state: 'WARN',
            message: 'Documento cifrado detectado por pre-scan (/Encrypt). Se bloquea sin montar el viewer.',
            event: 'PDF_ENCRYPTION_DETECTED_PRESCAN',
            args: { reason: '/Encrypt found' }
          });
        } catch {}
      } else {
        // No está cifrado: permitimos que el flujo normal continúe y el iframe se monte.
        setDocBlocked(false);
        setPdfNotProtected(true);
        // No seteamos viewer/document/draggable aquí: el viewer mjs enviará sus eventos habituales.
      }
    })();

    return () => { cancelled = true; };
  }, [blobUrl, setPdfNotProtected, setDocumentLoaded, setDraggableReady, setViewerLoaded]);


  useEffect(() => {
    setViewerLoaded(false);
    setDocumentLoaded(false);
    setDraggableReady(false);
    setPdfNotProtected(true);
    appearanceRef.current = false;
    appearanceAutoLoadRef.current = false;

    function handleMessage(event) {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'PDF_VIEWER_READY') {
        setViewerLoaded(true);
        logAudit({
          state: 'INFO',
          message: 'El visor se cargo correctamente',
          event: 'PDF_VIEWER_READY',
          args: { type: 'debug message' }
        });
      }
      if (event.data.type === 'PDF_ENCRYPTION_DETECTED') {
        setPdfNotProtected(false);   // esto dispara el warning global (SignDocument.jsx)
        setDocumentLoaded(false);
        setDraggableReady(false);
        setViewerLoaded(true);       // el iframe cargó, pero bloqueamos su uso
        setDocBlocked(true);         // ocultamos el iframe localmente
        logAudit({
          state: 'WARN',
          message: 'Documento cifrado con contraseña de apertura',
          event: 'PDF_ENCRYPTION_DETECTED',
          args: event.data.payload || {}
        });
        return; // no seguimos procesando
      }

      if (event.data.type === 'PDF_DOCUMENT_LOADED') {
        setDocumentLoaded(true);
        logAudit({
          state: 'INFO',
          message: 'El documento cargo correctamente',
          event: 'PDF_DOCUMENT_LOADED',
          args: { type: 'debug message' }
        });
        const payload = event.data.payload || {};
        const {
          isEditable,
          isEncrypted,
          permissions,
          canModifyAnnotations,
          canFillForms,
          // canAssemble, // disponible si querés usarlo
        } = payload;

        // --- Reglas de decisión (conservadoras) ---
        // 1) Cifrado → bloquear siempre.
        let shouldBlock = !!isEncrypted;

        // 2) DocMDP: si agregamos campo de firma (flujo actual), bloquear P=1 o P=2
        if (!shouldBlock && (docMDPLevel === 1 || docMDPLevel === 2)) {
          shouldBlock = true;
        }

        // 3) Permisos: para agregar campo de firma necesitamos:
        //    - lo que hoy ustedes llaman "isEditable" (mantener semántica actual)
        //    - y poder modificar anotaciones (canModifyAnnotations)
        if (!shouldBlock) {
          const canAnn =
              typeof canModifyAnnotations === 'boolean'
                  ? canModifyAnnotations
                  // fallback por si el visor no envía el boolean (versiones previas)
                  : (Array.isArray(permissions) ? permissions.includes(32) : true);

          if (!isEditable || !canAnn) {
            shouldBlock = true;
          }
        }

        if (shouldBlock) {
          try {
            logAudit({
              state: 'WARN',
              message: 'Documento bloqueado por políticas (DocMDP/permisos)',
              event: 'PDF_DOCUMENT_BLOCKED',
              args: {
                isEncrypted: !!isEncrypted,
                docMDPLevel: docMDPLevel ?? null,
                isEditable: !!isEditable,
                canModifyAnnotations: !!canModifyAnnotations,
                permissions: Array.isArray(permissions) ? permissions : null
              }
            });
          } catch {}
          setPdfNotProtected(false); // dispara warning y bloquea botones en toda la UI
          setDocBlocked(true);       // no renderizamos el iframe
        } else {
          setPdfNotProtected(true);
          setDocBlocked(false);
        }
      }
      if (event.data.type === 'PDF_DRAGGABLE_READY') {
        if (!appearanceAutoLoadRef.current) {
          appearanceAutoLoadRef.current = true;
          (async () => {
            try {
              const list = await getSignatureAppearances();
              const selected = Array.isArray(list) ? list.find((item) => item?.isSelected) : null;
              let dataUrl = null;
              if (selected) {
                const configAppearance = selected?.configJson?.appearance || {};
                if (selected?.htmlTemplate) {
                  sessionStorage.setItem('signatureAppearanceHtml', selected.htmlTemplate);
                  try {
                    const width = Number(configAppearance?.width);
                    const hostWidth = Number.isFinite(width) && width > 0 ? Math.round(width / 0.9) : 420;
                    const regenerated = await renderHtmlTemplateToPNG(selected.htmlTemplate, hostWidth);
                    if (regenerated) {
                      configAppearance.image = configAppearance.image || {};
                      configAppearance.image.value = regenerated;
                      try {
                        const dims = await getImageDimensionsFromDataUrl(regenerated);
                        if (dims?.width > 0 && dims?.height > 0) {
                          const aspect = dims.width / dims.height;
                          const rawHeight = Number(configAppearance?.height);
                          const normalizedHeight = Number.isFinite(rawHeight) && rawHeight > 0
                            ? Math.round(rawHeight)
                            : 131;
                          configAppearance.baseW = dims.width;
                          configAppearance.baseH = dims.height;
                          configAppearance.imageAspect = aspect;
                          configAppearance.height = normalizedHeight;
                          configAppearance.width = Math.max(1, Math.round(normalizedHeight * aspect));
                        }
                      } catch {}
                    }
                  } catch (err) {
                    console.error('No se pudo regenerar signatureAppearance desde htmlTemplate.', err);
                  }
                } else {
                  sessionStorage.removeItem('signatureAppearanceHtml');
                }
                dataUrl = configAppearance?.image?.value || null;
                sessionStorage.setItem('appearance', JSON.stringify(configAppearance));
                if (dataUrl) {
                  sessionStorage.setItem('signatureAppearance', dataUrl);
                } else {
                  sessionStorage.removeItem('signatureAppearance');
                }
              }

              if (!dataUrl) {
                dataUrl = sessionStorage.getItem('signatureAppearance');
              }

              if (dataUrl && iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage({
                  type: 'SET_SIGNATURE_APPEARANCE',
                  imageData: dataUrl,
                  minScale,
                  maxScale
                }, '*');
              }
            } catch (err) {
              console.error('No se pudieron cargar las apariencias de firma.', err);
              const fallback = sessionStorage.getItem('signatureAppearance');
              if (fallback && iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage({
                  type: 'SET_SIGNATURE_APPEARANCE',
                  imageData: fallback,
                  minScale,
                  maxScale
                }, '*');
              }
            } finally {
              appearanceRef.current = true;
            }
          })();
        }
        setDraggableReady(true);
        logAudit({
          state: 'INFO',
          message: 'El campo de firma cargo correctamente',
          event: 'PDF_DRAGGABLE_READ',
          args: { type: 'debug message' }
        });
      }
      if (event.data.type === 'OPEN_SIGNATURE_APPEARANCE_MODAL') {
        let selectionEnabled = true;
        try {
          const encoded = sessionStorage.getItem('configs');
          if (encoded) {
            const cfg = JSON.parse(atob(encoded));
            const v = cfg?.SIGNATURE_APPEARANCE_MODAL;
            selectionEnabled = (v === undefined ? true : !!v);
          }
        } catch {}

        if (!selectionEnabled) {
          window.dispatchEvent(new CustomEvent('open-define-appearance-modal'));
        } else {
          const customAppearanceDefined = sessionStorage.getItem('customAppearanceDefined') === 'true';
          if (customAppearanceDefined) {
            window.dispatchEvent(new CustomEvent('open-define-appearance-modal'));
          } else {
            window.dispatchEvent(new CustomEvent('open-signature-appearance-modal'));
          }
        }
      }
    }
    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [blobUrl, setViewerLoaded, setDocumentLoaded, setDraggableReady]);

  useEffect(() => {
    let abort = false;

    async function scanDocMDP() {
      try {
        if (!blobUrl) return;
        const res = await fetch(blobUrl);
        const buf = await res.arrayBuffer();
        // Usamos latin1 para no romper con bytes binarios
        const text = new TextDecoder('latin1').decode(buf);

        let level = null;
        const idx = text.indexOf('/DocMDP');
        if (idx !== -1) {
          const pIdx = text.indexOf('/P', idx);
          if (pIdx !== -1) {
            const slice = text.slice(pIdx, pIdx + 20);
            const m = slice.match(/\/P\s+(\d)/);
            if (m) level = parseInt(m[1], 10);
          }
        }
        if (!abort) setDocMDPLevel(Number.isInteger(level) ? level : null);
      } catch {
        if (!abort) setDocMDPLevel(null);
      }
    }

    scanDocMDP();
    return () => { abort = true; };
  }, [blobUrl]);


  function handleIframeLoad() {
    logAudit({
      state: 'INFO',
      message: 'El IFrame cargo correctamente',
      event: 'IFRAME_LOADED',
      args: { type: 'debug message' }
    });
    sendPageModeToViewer();
  }

  return (
    <div className="pdfViewerContainer">
      {!docBlocked ? (
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          width="100%"
          height="750px"
          style={{ border: 'none' }}
          title={fileName}
          onLoad={handleIframeLoad}
        ></iframe>
      ) : (
        <p></p>
      )}
    </div>
  );
};

export default PdfViewer;
